import { useState, useEffect, useCallback } from 'react';
import { Calendar, Loader2, CheckCircle2, Clock, AlertCircle, Building2, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  confirmed: { label: 'Confirmé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  pending_payment: { label: 'Paiement en attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  pending: { label: 'En attente', color: 'bg-blue-100 text-blue-700', icon: Clock },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const getStatus = (booking) => {
  const s = booking.payment_status || booking.status || 'pending';
  return statusConfig[s] || statusConfig.pending;
};

const categoryLabels = {
  entreprises: 'Entreprise',
  associations: 'Association',
  etudiants: 'Étudiant/UPF',
};

const formulaLabels = {
  hourly: '1 heure',
  halfDay: 'Demi-journée',
  fullDay: 'Journée',
};

const AdminBookingsTab = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, { headers });
      setBookings(res.data || []);
    } catch {
      toast.error('Erreur chargement des réservations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const filtered = filter === 'all'
    ? bookings
    : bookings.filter(b => (b.payment_status || b.status) === filter);

  const confirmedBookings = bookings.filter(b => b.payment_status === 'paid' || b.status === 'confirmed');
  const pendingPayment = bookings.filter(b => b.status === 'pending_payment');
  const totalConfirmedRevenue = confirmedBookings.reduce((sum, b) => {
    const price = typeof b.price === 'string' ? parseInt(b.price.replace(/\s/g, ''), 10) : (b.price || 0);
    return sum + price;
  }, 0);

  const formatPrice = (price) => {
    if (!price) return '-';
    const num = typeof price === 'string' ? parseInt(price.replace(/\s/g, ''), 10) : price;
    return isNaN(num) ? price + ' F' : num.toLocaleString('fr-FR') + ' F';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div data-testid="bookings-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-total-bookings">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-2">
            <Calendar className="w-3.5 h-3.5" /> RÉSERVATIONS
          </div>
          <p className="text-2xl font-bold text-slate-900">{bookings.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-confirmed-bookings">
          <div className="flex items-center gap-2 text-emerald-600 text-xs font-medium mb-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> CONFIRMÉES
          </div>
          <p className="text-2xl font-bold text-emerald-700">{confirmedBookings.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-pending-bookings">
          <div className="flex items-center gap-2 text-amber-600 text-xs font-medium mb-2">
            <Clock className="w-3.5 h-3.5" /> PAIEMENT EN ATTENTE
          </div>
          <p className="text-2xl font-bold text-amber-700">{pendingPayment.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-bookings-revenue">
          <div className="flex items-center gap-2 text-teal-600 text-xs font-medium mb-2">
            <CreditCard className="w-3.5 h-3.5" /> REVENUS SALLES
          </div>
          <p className="text-2xl font-bold text-teal-700">{totalConfirmedRevenue > 0 ? totalConfirmedRevenue.toLocaleString('fr-FR') + ' F' : '0 F'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {[
            { id: 'all', label: 'Toutes' },
            { id: 'confirmed', label: 'Confirmées' },
            { id: 'pending_payment', label: 'Paiement en attente' },
            { id: 'pending', label: 'En attente' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filter === f.id ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`filter-bookings-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-slate-500">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune réservation</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Date réservation</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Client</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Salle</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Catégorie</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Formule</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Tarif</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => {
                  const st = getStatus(b);
                  const Icon = st.icon;
                  return (
                    <tr key={b.id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`booking-row-${i}`}>
                      <td className="p-3 text-slate-500 whitespace-nowrap">
                        {b.date || (b.created_at ? new Date(b.created_at).toLocaleDateString('fr-FR') : '-')}
                      </td>
                      <td className="p-3">
                        <p className="font-medium text-slate-900">{b.name || '-'}</p>
                        <p className="text-xs text-slate-400">{b.email || ''}{b.company ? ` · ${b.company}` : ''}</p>
                      </td>
                      <td className="p-3 text-slate-700">{b.room || '-'}</td>
                      <td className="p-3 text-slate-700">{categoryLabels[b.category] || b.category || '-'}</td>
                      <td className="p-3 text-slate-700">{formulaLabels[b.formula] || b.formula || '-'}</td>
                      <td className="p-3 text-right font-semibold text-slate-900 whitespace-nowrap">
                        {formatPrice(b.price)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${st.color}`}>
                          <Icon className="w-3 h-3" />
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingsTab;
